// Tuning bench for the blackout interaction - local mock state, no API. The components, hooks and
// offset mapping below are the SAME modules the real submission editor uses, so what is tuned here
// is what ships. Strings are hardcoded English on purpose: this page is never translated.

import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import {Alert, Box, Button, Card, Container, Divider, Stack, Typography} from "@mui/material";
import {alpha, Theme} from "@mui/material/styles";
import {Fragment, useRef, useState} from "react";
import SurveyBlackoutBar, {surveyBlackoutTextSx} from "../../surveys/submissions/blackout/SurveyBlackoutBar";
import SurveyBlackoutConfirmBubble from "../../surveys/submissions/blackout/SurveyBlackoutConfirmBubble";
import {
    clearTextSelection,
    surveyBlackoutSelectionRect
} from "../../surveys/submissions/blackout/surveyBlackoutOffsets";
import {
    buildSurveyBlackoutSegments,
    mergeSurveyBlackoutRange,
    SurveyBlackoutRange
} from "../../surveys/submissions/blackout/surveyBlackoutSegments";
import {
    useSurveyBlackoutArming
} from "../../surveys/submissions/blackout/useSurveyBlackoutArming";
import {
    useSurveyBlackoutSelection
} from "../../surveys/submissions/blackout/useSurveyBlackoutSelection";

/** Multiline, umlauts, emoji, and the kind of detail staff actually black out. */
const SAMPLE_TEXT = [
    "Das Frühstück war fantastisch 🥐 und Frau Müller am Empfang hat sich wirklich um alles gekümmert.",
    "Nur die Rechnung stimmte hinten und vorne nicht — bitte an simon.keller@example.com schicken.",
    "Zimmer 214, IBAN AT61 1904 3002 3457 3201, Tel. +43 664 1234567.",
    "Ansonsten kommen wir sicher wieder 😊",
].join("\n");

/** What the server writes over a hidden range. */
const MASK = "********";

/** The bench has a single text container; the editor registers one per answer field. */
const SAMPLE_KEY = "sample";

const monospaceSx = (theme: Theme) => ({
    p: 1.5,
    borderRadius: 1,
    bgcolor: alpha(theme.palette.text.primary, 0.04),
    fontFamily: "monospace",
    fontSize: "0.75rem",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
});

export const BlackoutTestPage = () => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    const [ranges, setRanges] = useState<SurveyBlackoutRange[]>([]);
    const sequenceRef = useRef(0);

    const {selection, clearSelection} = useSurveyBlackoutSelection({
        getContainers: () => [{key: SAMPLE_KEY, element: containerRef.current}],
        ignoreRef: bubbleRef,
    });

    const {armedId, handleBarClick, disarm} = useSurveyBlackoutArming({
        onRestore: barId => setRanges(current => current.filter(range => range.id !== barId)),
    });

    const applySelection = () => {
        if (!selection) return;

        setRanges(current => mergeSurveyBlackoutRange(current, selection.offsets, `mock-${++sequenceRef.current}`));
        clearTextSelection();
        clearSelection();
        disarm();
    };

    const segments = buildSurveyBlackoutSegments(SAMPLE_TEXT, ranges);
    const maskedText = segments.map(segment => (segment.range ? MASK : segment.text)).join("");

    return (
        <Container maxWidth="md" sx={{py: 4, overflowY: "auto"}}>
            <Stack gap={1.5} sx={{mb: 3}}>
                <Typography variant="h5" fontWeight={600}>Blackout interaction bench</Typography>

                <Alert severity="info" icon={<ScienceOutlinedIcon/>}>
                    Mock state only, no API — but the interaction is the shipped one: the same bar,
                    bubble, selection hook and offset mapper the submission blackout editor renders.
                </Alert>

                <Typography variant="body2" color="text.secondary">
                    Select any part of the answer — drag across it, double-click a word, triple-click a
                    line — then confirm with the button at the end of the selection. Click a bar once to
                    arm it, again to restore it, anywhere else to call it off. Height check: compare a
                    live selection with a bar — the blue highlight and the black block should be exactly
                    the same height.
                </Typography>
            </Stack>

            <Card variant="outlined" sx={{p: 2.5, borderRadius: 3, display: "flex", flexDirection: "column", gap: 2}}>
                <Box
                    ref={containerRef}
                    sx={{...surveyBlackoutTextSx, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider"}}
                >
                    {segments.map(segment => {
                        const range = segment.range;
                        if (!range) return <Fragment key={segment.key}>{segment.text}</Fragment>;

                        return (
                            <SurveyBlackoutBar
                                key={segment.key}
                                barId={range.id}
                                text={segment.text}
                                armed={armedId === range.id}
                                tooltip={armedId === range.id ? "Click again to restore" : "Click to restore"}
                                onClick={() => handleBarClick(range.id)}
                            />
                        );
                    })}
                </Box>

                <Divider/>

                <Box>
                    <Typography variant="caption" color="text.secondary">
                        {`Masked value (${ranges.length} ${ranges.length === 1 ? "range" : "ranges"})`}
                    </Typography>
                    <Box sx={monospaceSx}>{maskedText}</Box>
                </Box>

                <Box>
                    <Button
                        size="small"
                        onClick={() => {
                            setRanges([]);
                            clearSelection();
                            disarm();
                        }}
                        disabled={!ranges.length}
                    >
                        Reset
                    </Button>
                </Box>
            </Card>

            {selection && (
                <SurveyBlackoutConfirmBubble
                    getAnchorRect={() => surveyBlackoutSelectionRect(containerRef.current) || selection.anchor}
                    contextElement={containerRef.current}
                    bubbleRef={bubbleRef}
                    label="Black out"
                    onApply={applySelection}
                />
            )}
        </Container>
    );
};

export default BlackoutTestPage;
